import React from "react";
import { MapPin, Phone, Mail, Clock, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { ouvrirPreferences } from "../utils/consent";

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <img 
                src="/images/logo-marianne.svg"
                width="24"
                height="24"
                loading="lazy" 
                alt="Transition Énergie France Logo" 
                className="w-10 h-10 object-contain bg-white rounded p-1"
              />
              <div>
                <h3 className="text-lg font-bold">Transition Énergie France</h3>
                <p className="text-orange-400 text-sm font-medium">AMO Ma Prime Adapt</p>
              </div>
            </div>
            
            <p className="text-gray-300 leading-relaxed">
              Assistant à maîtrise d'ouvrage agréé ANAH pour l'adaptation des logements 
              au vieillissement et au handicap en Île-de-France.
            </p>
            
            <div className="flex space-x-4">
              <div className="w-8 h-8 bg-orange-700 rounded-full flex items-center justify-center text-xs font-bold">
                AMO
              </div>
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-xs font-bold">
                RGE
              </div>
              <div className="w-8 h-8 bg-green-700 rounded-full flex items-center justify-center text-xs font-bold">
                IDF
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Nos services</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/simulateur" className="inline-flex items-center min-h-[44px] text-gray-300 hover:text-orange-400 transition-colors">
                  Ma Prime Adapt
                </Link>
              </li>
              <li>
                <Link to="/salle-de-bain" className="inline-flex items-center min-h-[44px] text-gray-300 hover:text-orange-400 transition-colors">
                  Adaptation salle de bain
                </Link>
              </li>
              <li>
                <Link to="/accessibilite" className="inline-flex items-center min-h-[44px] text-gray-300 hover:text-orange-400 transition-colors">
                  Solutions d'accessibilité
                </Link>
              </li>
              <li>
                <Link to="/monte-escalier" className="inline-flex items-center min-h-[44px] text-gray-300 hover:text-orange-400 transition-colors">
                  Monte-escalier
                </Link>
              </li>
              <li>
                <Link to="/exterieurs" className="inline-flex items-center min-h-[44px] text-gray-300 hover:text-orange-400 transition-colors">
                  Accès et extérieurs adaptés
                </Link>
              </li>
              <li>
                <Link to="/aides-complementaires" className="inline-flex items-center min-h-[44px] text-gray-300 hover:text-orange-400 transition-colors">
                  Aides complémentaires
                </Link>
              </li>
              <li>
                <Link to="/maprimeadapt" className="inline-flex items-center min-h-[44px] text-gray-300 hover:text-orange-400 transition-colors">
                  Accompagnement AMO
                </Link>
              </li>
              <li>
                <Link to="/plafonds-ressources-maprimeadapt" className="inline-flex items-center min-h-[44px] text-gray-300 hover:text-orange-400 transition-colors">
                  Plafonds de ressources
                </Link>
              </li>
              <li>
                <Link to="/aidants" className="inline-flex items-center min-h-[44px] text-gray-300 hover:text-orange-400 transition-colors">
                  Vous aidez un proche
                </Link>
              </li>
              <li>
                <Link to="/blog" className="inline-flex items-center min-h-[44px] text-gray-300 hover:text-orange-400 transition-colors">
                  Guides MaPrimeAdapt'
                </Link>
              </li>
              {/* /contact et /avis-maprimeadapt n'avaient aucun lien entrant :
                  ni Google ni un visiteur ne pouvaient les atteindre en naviguant. */}
              <li>
                <Link to="/avis-maprimeadapt" className="inline-flex items-center min-h-[44px] text-gray-300 hover:text-orange-400 transition-colors">
                  Avis MaPrimeAdapt'
                </Link>
              </li>
              <li>
                <Link to="/contact" className="inline-flex items-center min-h-[44px] text-gray-300 hover:text-orange-400 transition-colors">
                  Nous contacter
                </Link>
              </li>
              <li>
                <Link to="/acces-pro" className="inline-flex items-center min-h-[44px] text-gray-300 hover:text-orange-400 transition-colors">
                  Espace artisans partenaires
                </Link>
              </li>
            </ul>
          </div>

          {/* Zone d'intervention */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Zone d'intervention</h4>
            <div className="space-y-3">
              <div>
                <p className="text-orange-400 text-sm font-medium mb-2">Actuellement disponible :</p>
                {/* Liens vers les pages départementales : maillage interne */}
                <ul className="text-sm">
                  {[
                    { nom: "Paris (75)", url: "/amo-anah-paris-75" },
                    { nom: "Seine-et-Marne (77)", url: "/amo-anah-seine-et-marne-77" },
                    { nom: "Essonne (91)", url: "/amo-anah-essonne-91" },
                    { nom: "Hauts-de-Seine (92)", url: "/amo-anah-hauts-de-seine-92" },
                    { nom: "Seine-Saint-Denis (93)", url: "/amo-anah-seine-saint-denis-93" },
                    { nom: "Val-d'Oise (95)", url: "/amo-anah-val-doise-95" },
                  ].map((d) => (
                    <li key={d.url}>
                      <Link to={d.url} className="inline-flex items-center min-h-[44px] text-gray-300 hover:text-orange-400 transition-colors">
                        {d.nom}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-yellow-400 text-sm font-medium mb-2">Ouverture prévue :</p>
                <ul className="space-y-1 text-sm">
                  <li className="text-gray-400">Yvelines (78)</li>
                  <li className="text-gray-400">Val-de-Marne (94)</li>
                  <li className="text-gray-400">Oise (60)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Contact</h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Phone className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">09 39 03 54 44</p>
                  <p className="text-sm text-gray-400">Service & appel gratuits</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p>contact@transition-energie-france.fr</p>
                  <p className="text-sm text-gray-400">Réponse sous 24h</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Clock className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm">Lun-Jeu : 10h-17h</p>
                  <p className="text-sm">Ven : 10h-13h</p>
                  <p className="text-sm text-gray-400">Fermé weekend</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm">Siège social</p>
                  <p className="text-sm text-gray-400">99 avenue Achille Peretti</p>
                  <p className="text-sm text-gray-400">92200 Neuilly-sur-Seine</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom section */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-gray-400">
              <span>© 2026 Transition Énergie France</span>
              <span>SIRET : 880 084 694 00046</span>
              <span>AMO agréé ANAH</span>
            </div>
            
            {/* py-3 : cibles tactiles d'au moins 44px de haut sur mobile */}
            <div className="flex flex-wrap justify-center gap-x-6 text-sm">
              <Link to="/mentions-legales" className="py-3 text-gray-400 hover:text-orange-400 transition-colors">
                Mentions légales
              </Link>
              <Link to="/politique-confidentialite" className="py-3 text-gray-400 hover:text-orange-400 transition-colors">
                Politique de confidentialité
              </Link>
              <button
                type="button"
                onClick={ouvrirPreferences}
                className="py-3 text-gray-400 hover:text-orange-400 transition-colors"
              >
                Gérer mes cookies
              </button>
              <a
                href="https://www.anah.gouv.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="py-3 text-orange-400 hover:text-orange-300 flex items-center transition-colors"
              >
                Site ANAH
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </div>
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-300">
              Ma Prime Adapt est une aide de l'Agence nationale de l'habitat (Anah) pour financer les travaux d'adaptation
              du logement au vieillissement ou au handicap. Cette aide est réservée aux propriétaires occupants et aux 
              locataires du parc privé sous conditions de ressources.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;